import React, { useState } from "react";
import { Sun, Moon } from "lucide-react";
import PackerBasePaint from "./PackerBasePaint";
import PackerCorrectionDialog from "./PackerCorrectionDialog";
import type { PackerLineItem as LineItemType, PackerBasePaint as BasePaintType } from "@/hooks/usePackers";

interface PackerLineItemProps {
  item: LineItemType;
}

const colorTypeBadge = (type: string | null | undefined) => {
  if (!type) return null;
  if (type === "light") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-yellow-500/20 border-yellow-400/60 text-yellow-300 text-[10px] font-medium">
        <Sun className="w-3 h-3" />
        licht
      </span>
    );
  }
  if (type === "dark") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-purple-500/20 border-purple-400/60 text-purple-300 text-[10px] font-medium">
        <Moon className="w-3 h-3" />
        donker
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-white/10 border-white/20 text-white/70 text-[10px] font-medium">
      {type}
    </span>
  );
};

const PackerLineItem = ({ item }: PackerLineItemProps) => {
  const [overridePaint, setOverridePaint] = useState<BasePaintType | null>(null);
  const [correctionOpen, setCorrectionOpen] = useState(false);

  if (item.base_paint?.ignored_brand) return null;

  const hasMultiple = item.quantity > 1;
  const activePaint = overridePaint ?? item.base_paint;

  const handleCorrectionSelect = (selected: { product_naam: string; eenheid: string; locatie: string; voorraad: number }) => {
    setOverridePaint({
      product_naam: selected.product_naam,
      eenheid: selected.eenheid,
      locatie: selected.locatie,
      voorraad: selected.voorraad,
      is_out_of_stock: selected.voorraad <= 0,
    });
  };

  return (
    <div
      className={`py-2 border-b last:border-0 ${
        hasMultiple
          ? "bg-red-500/10 border-red-500/30 rounded-lg px-2 -mx-2 my-1"
          : "border-white/5"
      }`}
    >
      <div className="flex items-start gap-3">
        {item.image_url && (
          <img
            src={item.image_url}
            alt={item.producttitle}
            className="w-10 h-10 rounded object-cover shrink-0 bg-white/10"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-medium text-sm truncate">{item.producttitle}</span>
            {hasMultiple && (
              <span className="bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded animate-pulse">
                ⚠ ×{item.quantity}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {item.varianttitle && (
              <span className="bg-white/10 text-white/70 px-1.5 py-0.5 rounded text-[10px] font-medium">
                {item.varianttitle.split(' - ')[0]?.trim() || item.varianttitle}
              </span>
            )}
            {!item.varianttitle && item.volume && (
              <span className="bg-white/10 text-white/70 px-1.5 py-0.5 rounded text-[10px]">
                {item.volume}
              </span>
            )}
            {item.hex_color && (
              <span
                className="w-4 h-4 rounded-full border border-white/30 shrink-0"
                style={{ backgroundColor: item.hex_color }}
              />
            )}
            {item.color_code && (
              <span className="text-white/60 text-xs">{item.color_code}</span>
            )}
            {colorTypeBadge(item.color_type)}
            {item.collection_name && (
              <span className="text-blue-300 text-[10px] font-medium">{item.collection_name}</span>
            )}
          </div>
        </div>
      </div>
      <PackerBasePaint
        basePaint={activePaint}
        isOverridden={!!overridePaint}
        onCorrection={() => setCorrectionOpen(true)}
      />
      <PackerCorrectionDialog
        open={correctionOpen}
        onOpenChange={setCorrectionOpen}
        onSelect={handleCorrectionSelect}
        productTitle={item.producttitle}
      />
    </div>
  );
};

export default PackerLineItem;
